import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Search, Star, Layers, MessageSquare, Users, Zap, Shield, FileText,
  PieChart as PieIcon, LayoutGrid, User, Activity, Network,
  Clock, AlertTriangle, CheckCircle, Grid3x3, Trophy,
} from 'lucide-react';
import vegaEmbed from 'vega-embed';
import { useSettings } from '../hooks/useSettings';
import { appConfig } from '../config/AppConfig';

// System icon name (from the pack's account_360.json) → component.
const ICONS = { 'message-square': MessageSquare, users: Users, zap: Zap, shield: Shield, 'file-text': FileText, activity: Activity, star: Star };
// KPI metric icon name → component.
const METRIC_ICONS = { layers: Layers, zap: Zap, clock: Clock, 'file-text': FileText, 'alert-triangle': AlertTriangle, star: Star, network: Network, activity: Activity };

// Format a KPI value by its declared format.
function fmtMetric(v, format, isAr) {
  if (v === null || v === undefined || v === '') return '—';
  const loc = isAr ? 'ar-EG' : 'en-US';
  if (format === 'money') {
    const n = Number(v);
    return (isFinite(n) ? n.toLocaleString(loc, { maximumFractionDigits: 2 }) : String(v)) + (isAr ? ' ﷼' : ' SAR');
  }
  if (format === 'int' || format === 'num') {
    const n = Number(v);
    return isFinite(n) ? n.toLocaleString(loc, { maximumFractionDigits: 1 }) : String(v);
  }
  return String(v);
}

// Map a status string to a semantic color (green=resolved, amber=in-progress, red=escalated).
function statusColor(status) {
  if (!status) return null;
  const s = String(status).toLowerCase();
  if (/(clos|resolv|complet|done|paid|مغلق|منجز|مكتمل|محلول)/.test(s)) return '#16a34a';
  if (/(reopen|escalat|overdue|reject|معاد|متأخر|مرفوض)/.test(s)) return '#dc2626';
  if (/(open|progress|pending|verif|assign|enroute|جار|مفتوح|قيد|معلّق|متابعة)/.test(s)) return '#d97706';
  return null;
}

// Interaction timeline — every event as a dot on a time axis, one lane per system.
function AccountTimeline({ timeline, systems, isAr }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !timeline || timeline.length === 0) return undefined;
    const meta = {};
    (systems || []).forEach((s) => { meta[s.key] = { label: isAr ? s.label_ar : s.label_en, color: s.color }; });
    const data = timeline.filter((e) => e.ts && meta[e.sys]).map((e) => ({ ts: e.ts, sys: meta[e.sys].label }));
    if (data.length === 0) return undefined;
    const lanes = (systems || []).filter((s) => meta[s.key] && data.some((d) => d.sys === meta[s.key].label));
    const order = lanes.map((s) => meta[s.key].label);
    const range = lanes.map((s) => s.color);
    const spec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      data: { values: data },
      width: 'container', height: Math.max(110, order.length * 30),
      mark: { type: 'circle', size: 95, opacity: 0.82, stroke: '#fff', strokeWidth: 1 },
      encoding: {
        x: { field: 'ts', type: 'temporal', title: null, axis: { grid: true, gridColor: '#f1f5f9', format: '%b %Y', labelColor: '#94a3b8' } },
        y: { field: 'sys', type: 'nominal', title: null, sort: order, axis: { labelFontSize: 12, labelColor: '#475569', domain: false, ticks: false } },
        color: { field: 'sys', type: 'nominal', scale: { domain: order, range }, legend: null },
        tooltip: [
          { field: 'sys', type: 'nominal', title: isAr ? 'النظام' : 'System' },
          { field: 'ts', type: 'temporal', title: isAr ? 'التاريخ' : 'Date', format: '%Y-%m-%d' },
        ],
      },
      config: { font: 'Inter, "Noto Sans Arabic", sans-serif', view: { stroke: null } },
    };
    let view;
    vegaEmbed(ref.current, spec, { actions: false }).then((r) => { view = r.view; }).catch(() => {});
    return () => { if (view) { try { view.finalize(); } catch (e) { /* noop */ } } };
  }, [timeline, systems, isAr]);
  return <div ref={ref} style={{ width: '100%', direction: 'ltr' }} />;
}
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const Container = styled.div`
  flex: 1; height: 100vh; overflow-y: auto; padding: 32px;
  background: #f8fafc;
`;
const PageHeader = styled.div`margin-bottom: 18px;`;
const PageTitle = styled.h1`
  font-size: 25px; font-weight: 800; color: #0f172a; margin: 0;
  display: flex; align-items: center; gap: 11px;
  svg { color: #1B8C3C; }
`;
const PageSubtitle = styled.p`color: #64748b; margin: 7px 0 0; font-size: 14px;`;

// Mode toggle: portfolio overview (all accounts) vs a single account.
const ModeTabs = styled.div`display: inline-flex; gap: 4px; background: #eef2f7; border-radius: 12px; padding: 4px; margin-bottom: 22px;`;
const ModeTab = styled.button`
  display: flex; align-items: center; gap: 7px; border: none; cursor: pointer; border-radius: 9px;
  padding: 9px 16px; font-size: 13.5px; font-weight: 700; transition: all .15s;
  background: ${p => (p.$on ? '#fff' : 'transparent')}; color: ${p => (p.$on ? '#006200' : '#64748b')};
  box-shadow: ${p => (p.$on ? '0 1px 3px rgba(0,0,0,0.1)' : 'none')};
  svg { width: 16px; height: 16px; }
`;

const Toolbar = styled.div`display: flex; align-items: center; gap: 12px; margin-bottom: 22px; flex-wrap: wrap;`;
const SearchBox = styled.div`
  display: flex; align-items: center; gap: 9px; background: #fff; border: 1px solid #e2e8f0;
  border-radius: 12px; padding: 11px 15px; min-width: 240px; flex: 1; max-width: 360px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04); transition: border-color .15s, box-shadow .15s;
  &:focus-within { border-color: rgba(27,140,60,0.5); box-shadow: 0 0 0 3px rgba(27,140,60,0.1); }
  svg { width: 18px; height: 18px; color: #94a3b8; }
  input { border: none; outline: none; flex: 1; font-size: 14px; background: transparent; color: #0f172a; }
`;
// Sort-by-metric dropdown for the account selector.
const SortSelect = styled.select`
  appearance: none; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 11px 34px 11px 14px; font-size: 13px; font-weight: 700; color: #0f172a; cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat; background-position: right 12px center;
  &:focus { outline: none; border-color: rgba(27,140,60,0.5); }
`;
const AccountSelect = styled.select`
  appearance: none; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 11px 36px 11px 15px; font-size: 14px; font-weight: 700; color: #0f172a; cursor: pointer;
  min-width: 250px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); direction: ltr;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat; background-position: right 12px center;
  &:focus { outline: none; border-color: rgba(27,140,60,0.5); box-shadow: 0 0 0 3px rgba(27,140,60,0.1); }
`;

// KPI cards (overview).

const AcctNo = styled.div`font-weight: 800; color: #0f172a; font-size: 15.5px; display: flex; align-items: center; gap: 8px;`;
const CrossBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px; background: #fef3c7; color: #92400e;
  font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 999px; svg { width: 11px; height: 11px; }
`;
const ProfileHead = styled.div`
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%);
  border: 1px solid rgba(27,140,60,0.25); border-radius: 18px; padding: 24px; margin-bottom: 20px;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
`;
const DashRow = styled.div`
  display: grid; grid-template-columns: minmax(290px, 1fr) 1.7fr; gap: 18px; margin-bottom: 24px; align-items: start;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const PieCard = styled.div`
  background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;
const CardTitle = styled.h3`
  margin: 0 0 14px; font-size: 14px; font-weight: 800; color: #0f172a; display: flex; align-items: center; gap: 8px;
  svg { color: #1B8C3C; }
`;
const Tiles = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px;`;
const TreeCard = styled.div`
  background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 24px;
`;
const TreeLegend = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px 16px; margin: 4px 0 12px; font-size: 12px; color: #64748b;
  span { display: inline-flex; align-items: center; gap: 6px; }
  i { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
`;
const Tile = styled.div`
  background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); opacity: ${p => (p.$muted ? 0.65 : 1)};
  transition: transform .15s, box-shadow .15s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(15,23,42,.07); }
`;
const TileTop = styled.div`display: flex; align-items: center; gap: 9px; color: #64748b; font-size: 13px; font-weight: 700;
  svg { width: 18px; height: 18px; color: ${p => p.$c || '#1B8C3C'}; }`;
const TileVal = styled.div`font-size: 30px; font-weight: 800; color: ${p => p.$c || '#0f172a'}; margin-top: 8px; line-height: 1.1;`;

// "Accounts by number of systems" bar list (overview).
const Bars = styled.div`display: flex; flex-direction: column; gap: 11px;`;
const BarRow = styled.div`display: grid; grid-template-columns: 150px 1fr 72px; align-items: center; gap: 12px;`;
const BarLabel = styled.div`font-size: 13px; font-weight: 700; color: #475569;`;
const BarTrack = styled.div`height: 22px; background: #f1f5f9; border-radius: 7px; overflow: hidden;`;
const BarFill = styled.div`height: 100%; background: linear-gradient(90deg, #1B8C3C, #0ea5e9); border-radius: 7px; transition: width .3s;`;
const BarNum = styled.div`font-size: 13px; font-weight: 700; color: #0f172a; text-align: end;`;

const SystemBlock = styled.div`background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 20px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);`;
const SystemTitle = styled.h3`margin: 0 0 14px; font-size: 15px; font-weight: 800; color: #0f172a; display: flex; align-items: center; gap: 8px;
  svg { width: 17px; height: 17px; color: ${p => p.$c}; }`;
// Wide record tables scroll horizontally inside their own container (page body never scrolls).
const TableScroll = styled.div`width: 100%; overflow-x: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: start; color: #94a3b8; font-weight: 700; padding: 8px 10px; border-bottom: 1px solid #eef2f7; white-space: nowrap; }
  td { padding: 9px 10px; border-bottom: 1px solid #f8fafc; color: #334155; white-space: nowrap; }
  tr:hover td { background: #f8fafc; }`;
// Small count badge shown next to a system's title.
const SystemCount = styled.span`margin-inline-start: auto; font-size: 12px; font-weight: 800; color: #64748b;
  background: #f1f5f9; border-radius: 999px; padding: 2px 10px;`;
const Center = styled.div`color: #94a3b8; padding: 48px; text-align: center;`;

// Account health chip (derived from KPIs): good / attention.
const HealthChip = styled.span`
  display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 800;
  padding: 4px 11px; border-radius: 999px; svg { width: 12px; height: 12px; }
  color: ${p => p.$tone === 'bad' ? '#9b1c1c' : p.$tone === 'warn' ? '#92400e' : '#065f46'};
  background: ${p => p.$tone === 'bad' ? '#fee2e2' : p.$tone === 'warn' ? '#fef3c7' : '#d1fae5'};
`;
// KPI strip.
const MetricStrip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 20px;
`;
const MetricCard = styled.div`
  background: #fff; border: 1px solid #eef2f7; border-radius: 14px; padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); border-top: 3px solid ${p => p.$c || '#94a3b8'};
`;
const MetricTop = styled.div`display: flex; align-items: center; gap: 7px; color: #64748b; font-size: 12px; font-weight: 700;
  svg { width: 14px; height: 14px; color: ${p => p.$c || '#94a3b8'}; }`;
const MetricVal = styled.div`font-size: 23px; font-weight: 800; color: ${p => p.$c || '#0f172a'}; margin-top: 6px; line-height: 1.15;`;
const TimelineCard = styled.div`
  background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 24px;
`;
// Records-per-account distribution (horizontal bar list — readable despite the extreme skew).
const DistRow = styled.div`display: grid; grid-template-columns: 92px 1fr auto; align-items: center; gap: 12px; padding: 5px 0;`;
const DistLabel = styled.div`font-size: 12.5px; font-weight: 700; color: #475569; text-align: start;`;
const DistTrack = styled.div`background: #f1f5f9; border-radius: 999px; height: 16px; overflow: hidden;`;
const DistBar = styled.div`height: 100%; border-radius: 999px; background: ${p => p.$c || '#1B8C3C'}; width: ${p => p.$w}%; min-width: 3px; transition: width .3s;`;
const DistCount = styled.div`font-size: 12.5px; font-weight: 800; color: #0f172a; min-width: 66px; text-align: end; font-variant-numeric: tabular-nums;`;
// Cross-system intersection heatmap.
const OverviewCard = styled.div`
  background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 24px;
`;
const HeatScroll = styled.div`width: 100%; overflow-x: auto;`;
const HeatGrid = styled.div`display: grid; gap: 3px; min-width: max-content; direction: ltr;`;
const HeatCorner = styled.div``;
const HeatColHead = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 4px; padding-bottom: 4px;
  font-size: 10.5px; font-weight: 700; color: #64748b; text-align: center; line-height: 1.1;
  i { width: 9px; height: 9px; border-radius: 3px; }
  span { max-width: 62px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;
const HeatRowHead = styled.div`
  display: flex; align-items: center; gap: 7px; font-size: 11.5px; font-weight: 700; color: #334155;
  padding-inline-end: 8px; white-space: nowrap;
  i { width: 10px; height: 10px; border-radius: 3px; flex: none; }
`;
const HeatCell = styled.div`
  display: flex; align-items: center; justify-content: center; height: 40px; border-radius: 6px;
  font-size: 11.5px; font-weight: 700; font-variant-numeric: tabular-nums; cursor: default;
`;
// Top-accounts leaderboard.
const LbHead = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap;`;
const LbTable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: start; color: #94a3b8; font-weight: 700; padding: 8px 10px; border-bottom: 1px solid #eef2f7; white-space: nowrap; }
  td { padding: 10px; border-bottom: 1px solid #f8fafc; color: #334155; white-space: nowrap; }
  tbody tr { cursor: pointer; transition: background .12s; }
  tbody tr:hover td { background: #f0fdf4; }
  .rank { color: #94a3b8; font-weight: 800; width: 34px; }
  .acct { font-weight: 800; color: #0f172a; font-variant-numeric: tabular-nums; }
  .val { font-weight: 800; text-align: end; font-variant-numeric: tabular-nums; }
`;

// Activity-across-systems pie (Vega). The chart div is forced to `direction:ltr` so the RTL page
// doesn't flip Vega's layout over the plot (same fix as the pinned/dashboard charts).
function AccountPie({ systems, isAr }) {
  const ref = useRef(null);
  useEffect(() => {
    const data = (systems || [])
      .filter((s) => s.linked && s.count > 0)
      .map((s) => ({ label: isAr ? s.label_ar : s.label_en, count: s.count, color: s.color }));
    if (!ref.current || data.length === 0) return undefined;
    const spec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      data: { values: data },
      mark: { type: 'arc', innerRadius: 58, cornerRadius: 3, stroke: '#fff', strokeWidth: 2 },
      encoding: {
        theta: { field: 'count', type: 'quantitative', stack: true },
        color: {
          field: 'label', type: 'nominal',
          scale: { range: data.map((d) => d.color) },
          legend: { title: null, orient: isAr ? 'left' : 'right' },
        },
        tooltip: [
          { field: 'label', type: 'nominal', title: isAr ? 'النظام' : 'System' },
          { field: 'count', type: 'quantitative', title: isAr ? 'العدد' : 'Count' },
        ],
      },
      width: 'container', height: 250,
      config: {
        font: 'Inter, "Noto Sans Arabic", sans-serif',
        view: { stroke: null },
        legend: { labelFontSize: 12.5, labelColor: '#475569' },
      },
    };
    let view;
    vegaEmbed(ref.current, spec, { actions: false }).then((r) => { view = r.view; }).catch(() => {});
    return () => { if (view) { try { view.finalize(); } catch (e) { /* noop */ } } };
  }, [systems, isAr]);
  return <div ref={ref} style={{ width: '100%', minHeight: 250, direction: 'ltr' }} />;
}

// Customer interaction map — a dendrogram rooted at the account: account → systems →
// recent records. Rendered LTR (like the pie) so Vega's tree layout isn't flipped by the RTL page.
function AccountTree({ profile, isAr }) {
  const ref = useRef(null);
  useEffect(() => {
    const linked = (profile.systems || []).filter((s) => s.linked && s.records && s.records.length > 0);
    if (!ref.current || linked.length === 0) return undefined;

    const PER = 5; // recent records shown per system (keeps the tree legible)
    const acctLabel = (isAr ? 'الحساب ' : 'Account ') + (profile.account_number || '');
    const nodes = [{ id: 'root', name: acctLabel, kind: 'root', color: '#0f172a', fill: '#0f172a', tip: acctLabel }];
    linked.forEach((s, si) => {
      const sid = 's' + si;
      const sysLabel = isAr ? s.label_ar : s.label_en;
      nodes.push({ id: sid, parent: 'root', name: `${sysLabel} (${s.count})`, kind: 'system', color: s.color, fill: s.color, count: s.count, tip: `${sysLabel}: ${s.count}` });
      s.records.slice(0, PER).forEach((r, ri) => {
        const d = (r.date || r.opened || r.closed || '');
        let leaf = String(r.ref || '—');
        if (d) leaf += '  ·  ' + String(d).slice(0, 10);
        const stat = r.status || r.detail || r.survey_stage || '';
        const tip = [(isAr ? 'المرجع: ' : 'Ref: ') + (r.ref || '—')];
        if (d) tip.push((isAr ? 'التاريخ: ' : 'Date: ') + String(d).slice(0, 10));
        if (stat) tip.push((isAr ? 'الحالة: ' : 'Status: ') + stat);
        if (r.duration) tip.push((isAr ? 'المدة: ' : 'Duration: ') + r.duration);
        if (r.total_amount) tip.push((isAr ? 'المبلغ: ' : 'Amount: ') + r.total_amount);
        nodes.push({ id: `${sid}_${ri}`, parent: sid, name: leaf, kind: 'leaf', color: s.color, fill: statusColor(stat) || s.color, recent: ri === 0, tip: tip.join('\n') });
      });
      if (s.count > Math.min(PER, s.records.length)) {
        const extra = s.count - Math.min(PER, s.records.length);
        nodes.push({ id: `${sid}_more`, parent: sid, name: (isAr ? `+${extra} أخرى` : `+${extra} more`), kind: 'more', color: s.color, fill: '#cbd5e1' });
      }
    });

    const leaves = nodes.filter((n) => n.kind === 'leaf' || n.kind === 'more').length;
    const height = Math.max(240, leaves * 26 + 30);
    const spec = {
      $schema: 'https://vega.github.io/schema/vega/v5.json',
      width: 780, height, padding: { left: 130, right: 40, top: 14, bottom: 14 },
      autosize: { type: 'fit', contains: 'padding' },
      data: [
        {
          name: 'tree', values: nodes,
          transform: [
            { type: 'stratify', key: 'id', parentKey: 'parent' },
            { type: 'tree', method: 'cluster', size: [{ signal: 'height' }, { signal: 'width - 200' }], as: ['y', 'x', 'depth', 'children'] },
          ],
        },
        {
          name: 'links', source: 'tree',
          transform: [
            { type: 'treelinks' },
            { type: 'linkpath', orient: 'horizontal', shape: 'diagonal', sourceX: 'source.x', sourceY: 'source.y', targetX: 'target.x', targetY: 'target.y' },
          ],
        },
      ],
      marks: [
        { type: 'path', from: { data: 'links' }, encode: { update: { path: { field: 'path' }, stroke: { field: 'target.color' }, strokeOpacity: { value: 0.4 }, strokeWidth: { value: 1.6 } } } },
        {
          type: 'symbol', from: { data: 'tree' },
          encode: {
            enter: { stroke: { value: '#fff' }, strokeWidth: { value: 1.5 } },
            update: {
              x: { field: 'x' }, y: { field: 'y' },
              fill: { field: 'fill' },
              size: { signal: "datum.kind==='root' ? 300 : (datum.kind==='system' ? 120 + sqrt(datum.count) * 45 : (datum.recent ? 120 : 60))" },
              tooltip: { signal: 'datum.tip' },
            },
          },
        },
        {
          type: 'text', from: { data: 'tree' },
          encode: {
            enter: { baseline: { value: 'middle' } },
            update: {
              x: { field: 'x' }, y: { field: 'y' },
              dx: { signal: "datum.children ? -9 : 9" },
              align: { signal: "datum.children ? 'right' : 'left'" },
              text: { field: 'name' },
              fontSize: { signal: "datum.kind==='root'?13:(datum.kind==='system'?12:11)" },
              fontWeight: { signal: "datum.kind==='leaf' ? (datum.recent ? 'bold' : 'normal') : (datum.kind==='more' ? 'normal' : 'bold')" },
              fill: { signal: "datum.kind==='leaf' ? '#475569' : (datum.kind==='more' ? '#94a3b8' : datum.color)" },
              limit: { value: 250 },
            },
          },
        },
      ],
      config: { font: 'Inter, "Noto Sans Arabic", sans-serif' },
    };
    let view;
    vegaEmbed(ref.current, spec, { actions: false, renderer: 'svg' }).then((r) => { view = r.view; }).catch(() => {});
    return () => { if (view) { try { view.finalize(); } catch (e) { /* noop */ } } };
  }, [profile, isAr]);
  return <div ref={ref} style={{ width: '100%', overflowX: 'auto', direction: 'ltr' }} />;
}

// Cross-system overlap: an N×N grid where cell (i,j) = accounts appearing in BOTH systems.
// Diagonal = each system's own account total; off-diagonal intensity = √-scaled overlap.
function IntersectionHeatmap({ intersection, isAr }) {
  const sys = (intersection && intersection.systems) || [];
  const matrix = (intersection && intersection.matrix) || [];
  const n = sys.length;
  if (!n) return null;
  let maxOff = 1;
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) if (i !== j) maxOff = Math.max(maxOff, matrix[i][j] || 0);
  const lbl = (s) => (isAr ? s.label_ar : s.label_en);
  return (
    <HeatScroll>
      <HeatGrid style={{ gridTemplateColumns: `minmax(150px, auto) repeat(${n}, minmax(50px, 1fr))` }}>
        <HeatCorner />
        {sys.map((s) => (
          <HeatColHead key={s.key} title={lbl(s)}><i style={{ background: s.color }} /><span>{lbl(s)}</span></HeatColHead>
        ))}
        {sys.map((s, i) => (
          <React.Fragment key={s.key}>
            <HeatRowHead><i style={{ background: s.color }} />{lbl(s)}</HeatRowHead>
            {matrix[i].map((v, j) => {
              const diag = i === j;
              const t = diag ? 1 : Math.sqrt((v || 0) / maxOff);
              const bg = diag ? '#0f172a' : (v ? `rgba(27,140,60,${(0.10 + t * 0.85).toFixed(3)})` : '#f8fafc');
              const fg = diag ? '#fff' : (t > 0.55 ? '#fff' : '#334155');
              return (
                <HeatCell key={j} style={{ background: bg, color: fg }}
                  title={`${lbl(sys[i])} ∩ ${lbl(sys[j])}: ${(v || 0).toLocaleString()}`}>
                  {v ? v.toLocaleString() : ''}
                </HeatCell>
              );
            })}
          </React.Fragment>
        ))}
      </HeatGrid>
    </HeatScroll>
  );
}

// Top-accounts leaderboard — ranked by a chosen metric; a row click drills into that account.
function Leaderboard({ isAr, sortOptions, apiBase, onOpen }) {
  const [sort, setSort] = useState('total_records');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const base = await apiBase();
        const res = await fetch(`${base}/account/search?sort=${sort}&limit=10`);
        const d = await res.json();
        if (alive) setRows(d.accounts || []);
      } catch (e) { if (alive) setRows([]); }
    })();
    return () => { alive = false; };
  }, [sort, apiBase]);
  const opt = (sortOptions || []).find((o) => o.key === sort);
  const valOf = (a) => {
    if (!opt) return a.total_records;
    const unit = isAr ? (opt.unit_ar || '') : (opt.unit_en || '');
    const v = fmtMetric(a[opt.key], opt.format, isAr);
    return unit ? `${v} ${unit}` : v;
  };
  return (
    <OverviewCard>
      <LbHead>
        <CardTitle style={{ margin: 0 }}><Trophy size={16} />{isAr ? 'أبرز الحسابات' : 'Top accounts'}</CardTitle>
        <SortSelect value={sort} onChange={(e) => setSort(e.target.value)}>
          {(sortOptions || []).map((o) => <option key={o.key} value={o.key}>{isAr ? o.label_ar : o.label_en}</option>)}
        </SortSelect>
      </LbHead>
      <div style={{ overflowX: 'auto' }}>
        <LbTable>
          <thead><tr>
            <th className="rank">#</th>
            <th>{isAr ? 'الحساب' : 'Account'}</th>
            <th style={{ textAlign: 'end' }}>{opt ? (isAr ? opt.label_ar : opt.label_en) : ''}</th>
            <th>{isAr ? 'الأنظمة' : 'Systems'}</th>
          </tr></thead>
          <tbody>
            {rows.map((a, i) => (
              <tr key={a.account_sk} onClick={() => onOpen(a)}>
                <td className="rank">{i + 1}</td>
                <td className="acct">{a.account_number}</td>
                <td className="val">{valOf(a)}</td>
                <td>{a.systems_count} {isAr ? 'نظام' : 'sys'}</td>
              </tr>
            ))}
          </tbody>
        </LbTable>
      </div>
    </OverviewCard>
  );
}

export default function AccountProfile({ isActive }) {
  const { language } = useSettings();
  const isAr = language === 'ar';
  const [mode, setMode] = useState('overview');     // 'overview' (all accounts) | 'single'
  const [overview, setOverview] = useState(null);
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('systems_count'); // mv_account_360 column
  const [sortOptions, setSortOptions] = useState([]);    // from the search response (domain pack)
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  const apiBase = useCallback(async () => { await appConfig.load(); return appConfig.getMainApiUrl(); }, []);

  const loadOverview = useCallback(async () => {
    try {
      const base = await apiBase();
      const res = await fetch(`${base}/account/overview`);
      setOverview(await res.json());
    } catch (e) { setOverview({ success: false }); }
  }, [apiBase]);

  const openAccount = useCallback(async (a) => {
    setSelected(a); setProfile(null); setLoading(true);
    try {
      const base = await apiBase();
      const res = await fetch(`${base}/account/360?account_sk=${a.account_sk}`);
      setProfile(await res.json());
    } catch (e) { setProfile({ success: false }); } finally { setLoading(false); }
  }, [apiBase]);

  // Always list ALL accounts (no cross-system filter). sort='systems' surfaces the
  // widest (breadth) accounts first; sort='records' surfaces the deepest (most records).
  const loadList = useCallback(async (search, sort) => {
    setLoading(true);
    try {
      const base = await apiBase();
      const res = await fetch(`${base}/account/search?q=${encodeURIComponent(search || '')}&cross_system_only=false&sort=${sort || 'systems_count'}&limit=50`);
      const data = await res.json();
      setAccounts(data.accounts || []);
      if (data.sort_options && data.sort_options.length) setSortOptions(data.sort_options);
    } catch (e) { setAccounts([]); } finally { setLoading(false); }
  }, [apiBase]);

  // On tab activation: load the overview, and the account list for the single-account selector.
  useEffect(() => {
    if (isActive) { loadOverview(); loadList(q, sortBy); }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isActive]);

  // Auto-select the first account so the single-account dashboard always shows content.
  useEffect(() => {
    if (accounts.length > 0 && (!selected || !accounts.find((a) => a.account_sk === selected.account_sk))) {
      openAccount(accounts[0]);
    } else if (accounts.length === 0) {
      setSelected(null); setProfile(null);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [accounts]);

  const onSearch = (val) => {
    setQ(val);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => loadList(val, sortBy), 300);
  };
  const changeSort = (s) => { if (s === sortBy) return; setSortBy(s); loadList(q, s); };


  return (
    <Container dir={isAr ? 'rtl' : 'ltr'}>
      <PageHeader>
        <PageTitle><Layers color="#1B8C3C" size={24} />{isAr ? 'حساب 360' : 'Account 360'}</PageTitle>
        <PageSubtitle>
          {isAr
            ? 'لوحة موحّدة — نظرة عامة على كل الحسابات، أو تعمّق في حساب واحد عبر الأنظمة.'
            : 'A unified dashboard — an overview of all accounts, or drill into one across systems.'}
        </PageSubtitle>
      </PageHeader>

      <ModeTabs>
        <ModeTab $on={mode === 'overview'} onClick={() => setMode('overview')}>
          <LayoutGrid />{isAr ? 'نظرة عامة (كل الحسابات)' : 'Overview (all accounts)'}
        </ModeTab>
        <ModeTab $on={mode === 'single'} onClick={() => setMode('single')}>
          <User />{isAr ? 'حساب مفرد' : 'Single account'}
        </ModeTab>
      </ModeTabs>

      {/* ── OVERVIEW: all accounts ───────────────────────────────────────────── */}
      {mode === 'overview' && (
        !overview ? <Center>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</Center>
          : !overview.success ? <Center>{isAr ? 'تعذّر تحميل النظرة العامة' : 'Could not load overview'}</Center>
          : (
            <>
              {overview.portfolio_kpis && overview.portfolio_kpis.length > 0 && (
                <MetricStrip>
                  {overview.portfolio_kpis.map((m) => {
                    const MI = METRIC_ICONS[m.icon] || Layers;
                    return (
                      <MetricCard key={m.key} $c={m.color}>
                        <MetricTop $c={m.color}><MI />{isAr ? m.label_ar : m.label_en}</MetricTop>
                        <MetricVal $c={m.color}>{fmtMetric(m.value, m.format, isAr)}</MetricVal>
                      </MetricCard>
                    );
                  })}
                </MetricStrip>
              )}
              <DashRow>
                <PieCard>
                  <CardTitle><PieIcon size={16} />{isAr ? 'النشاط حسب النظام' : 'Activity by system'}</CardTitle>
                  <AccountPie systems={overview.systems} isAr={isAr} />
                </PieCard>
                <PieCard>
                  <CardTitle><LayoutGrid size={16} />{isAr ? 'توزيع السجلات لكل حساب' : 'Records per account'}</CardTitle>
                  {(() => {
                    const rows = overview.by_records || [];
                    const max = Math.max(1, ...rows.map((r) => r.accounts));
                    return (
                      <div style={{ direction: isAr ? 'rtl' : 'ltr' }}>
                        {rows.map((r) => (
                          <DistRow key={r.bucket}>
                            <DistLabel>{r.bucket} {isAr ? (r.bucket === '1' ? 'سجل' : 'سجلات') : 'rec'}</DistLabel>
                            {/* sqrt scaling so tiny buckets stay visible next to the 1-record spike */}
                            <DistTrack><DistBar $w={Math.max(3, Math.sqrt(r.accounts / max) * 100)} $c="#1B8C3C" /></DistTrack>
                            <DistCount>{fmt(r.accounts)}</DistCount>
                          </DistRow>
                        ))}
                      </div>
                    );
                  })()}
                </PieCard>
              </DashRow>

              {overview.intersection && overview.intersection.systems && overview.intersection.systems.length > 0 && (
                <OverviewCard>
                  <CardTitle><Grid3x3 size={16} />{isAr ? 'التقاطع بين الأنظمة (حسابات مشتركة)' : 'Cross-system overlap (shared accounts)'}</CardTitle>
                  <PageSubtitle style={{ margin: '0 0 12px', fontSize: 12 }}>
                    {isAr ? 'كل خلية = عدد الحسابات الظاهرة في كلا النظامين؛ القُطر = إجمالي حسابات النظام.'
                          : 'Each cell = accounts appearing in both systems; the diagonal = each system’s own account total.'}
                  </PageSubtitle>
                  <IntersectionHeatmap intersection={overview.intersection} isAr={isAr} />
                </OverviewCard>
              )}

              <Leaderboard
                isAr={isAr}
                sortOptions={sortOptions}
                apiBase={apiBase}
                onOpen={(a) => { setMode('single'); openAccount(a); }}
              />
            </>
          )
      )}

      {/* ── SINGLE ACCOUNT: selector-driven ──────────────────────────────────── */}
      {mode === 'single' && (
        <>
          <Toolbar>
            <SearchBox>
              <Search />
              <input
                value={q}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={isAr ? 'ابحث برقم الحساب…' : 'Search by account number…'}
              />
            </SearchBox>
            <AccountSelect
              value={selected ? String(selected.account_sk) : ''}
              onChange={(e) => { const a = accounts.find((x) => String(x.account_sk) === e.target.value); if (a) openAccount(a); }}
              aria-label={isAr ? 'اختر الحساب' : 'Select account'}
            >
              {accounts.length === 0 && <option value="">{isAr ? 'لا توجد حسابات' : 'No accounts'}</option>}
              {accounts.map((a) => {
                const opt = sortOptions.find((o) => o.key === sortBy);
                let metric = '';
                if (opt) {
                  const unit = isAr ? (opt.unit_ar || '') : (opt.unit_en || '');
                  const v = fmtMetric(a[opt.key], opt.format, isAr);
                  metric = unit ? `${v} ${unit}` : v;
                }
                // Show systems-count as context unless that's already the sort metric.
                const ctx = (opt && opt.key !== 'systems_count')
                  ? ` · ${a.systems_count} ${isAr ? 'نظام' : 'sys'}` : '';
                return (
                  <option key={a.account_sk} value={String(a.account_sk)}>
                    {a.account_number}{metric ? ` — ${metric}` : ''}{ctx}
                  </option>
                );
              })}
            </AccountSelect>
            <SortSelect
              value={sortBy}
              onChange={(e) => changeSort(e.target.value)}
              aria-label={isAr ? 'ترتيب الحسابات' : 'Sort accounts'}
            >
              {(sortOptions.length ? sortOptions : [
                { key: 'systems_count', label_ar: 'الأكثر أنظمة', label_en: 'Most systems' },
                { key: 'total_records', label_ar: 'الأكثر سجلات', label_en: 'Most records' },
              ]).map((o) => (
                <option key={o.key} value={o.key}>{isAr ? o.label_ar : o.label_en}</option>
              ))}
            </SortSelect>
          </Toolbar>

          {loading && !profile ? <Center>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</Center>
            : !profile ? <Center>{isAr ? 'اختر حساباً من القائمة' : 'Select an account'}</Center>
            : !profile.success ? <Center>{isAr ? 'تعذّر تحميل الحساب' : 'Could not load account'}</Center>
            : (
              <>
                <ProfileHead>
                  <div>
                    <AcctNo style={{ fontSize: 20 }}>
                      {profile.account_number}
                      {profile.cross_system && <CrossBadge><Star />{isAr ? 'متعدد الأنظمة' : 'cross-system'}</CrossBadge>}
                      {(() => {
                        const mv = (k) => { const m = (profile.metrics || []).find((x) => x.key === k); return Number(m && m.value) || 0; };
                        const out = mv('outstanding'), openO = mv('open_outages');
                        const tone = (out > 0 && openO > 0) ? 'bad' : (out > 0 || openO > 0) ? 'warn' : 'good';
                        const HIcon = tone === 'good' ? CheckCircle : AlertTriangle;
                        const txt = tone === 'good' ? (isAr ? 'سليم' : 'Healthy')
                          : tone === 'warn' ? (isAr ? 'يحتاج متابعة' : 'Needs attention')
                            : (isAr ? 'حرج' : 'Critical');
                        return <HealthChip $tone={tone}><HIcon />{txt}</HealthChip>;
                      })()}
                    </AcctNo>
                    <PageSubtitle style={{ margin: '6px 0 0' }}>
                      {profile.systems_count} {isAr ? 'نظام' : 'systems'} · {profile.total_records} {isAr ? 'سجل' : 'records'}
                    </PageSubtitle>
                  </div>
                </ProfileHead>

                {profile.metrics && profile.metrics.length > 0 && (
                  <MetricStrip>
                    {profile.metrics.map((m) => {
                      const MI = METRIC_ICONS[m.icon] || Layers;
                      return (
                        <MetricCard key={m.key} $c={m.color}>
                          <MetricTop $c={m.color}><MI />{isAr ? m.label_ar : m.label_en}</MetricTop>
                          <MetricVal $c={m.color}>{fmtMetric(m.value, m.format, isAr)}</MetricVal>
                        </MetricCard>
                      );
                    })}
                  </MetricStrip>
                )}

                <DashRow>
                  <PieCard>
                    <CardTitle><PieIcon size={16} />{isAr ? 'توزيع النشاط عبر الأنظمة' : 'Activity across systems'}</CardTitle>
                    <AccountPie systems={profile.systems} isAr={isAr} />
                  </PieCard>
                  <Tiles>
                    {profile.systems.filter((s) => s.linked).map((s) => {
                      const Icon = ICONS[s.icon] || FileText;
                      return (
                        <Tile key={s.key} $c={s.color}>
                          <TileTop $c={s.color}><Icon />{isAr ? s.label_ar : s.label_en}</TileTop>
                          <TileVal $c={s.color}>{s.count}</TileVal>
                        </Tile>
                      );
                    })}
                    {/* HONESTY TILES (restored 2026-07-09): systems whose data exists but carries no
                        account key (connections, field tasks) are SHOWN muted with the backend's own
                        note — hiding them reads as "data lost" to a client who knows they sent it. */}
                    {profile.systems.filter((s) => !s.linked).map((s) => {
                      const Icon = ICONS[s.icon] || FileText;
                      return (
                        <Tile key={s.key} $c="#94a3b8" style={{ opacity: 0.65 }}>
                          <TileTop $c="#94a3b8"><Icon />{isAr ? s.label_ar : s.label_en}</TileTop>
                          <TileVal $c="#94a3b8" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                            {isAr ? (s.note_ar || 'غير قابل للربط بالحساب') : (s.note_en || 'Not linkable to an account')}
                          </TileVal>
                        </Tile>
                      );
                    })}
                  </Tiles>
                </DashRow>

                {profile.timeline && profile.timeline.length > 0 && (
                  <TimelineCard>
                    <CardTitle><Activity size={16} />{isAr ? 'الخط الزمني للتفاعلات' : 'Interaction timeline'}</CardTitle>
                    <AccountTimeline timeline={profile.timeline} systems={profile.systems} isAr={isAr} />
                  </TimelineCard>
                )}

                {profile.systems.some((s) => s.linked && s.records && s.records.length > 0) && (
                  <TreeCard>
                    <CardTitle><Network size={16} />{isAr ? 'خريطة تفاعلات العميل' : 'Customer interaction map'}</CardTitle>
                    <TreeLegend>
                      {profile.systems.filter((s) => s.linked && s.records && s.records.length > 0).map((s) => (
                        <span key={s.key}><i style={{ background: s.color }} />{isAr ? s.label_ar : s.label_en}</span>
                      ))}
                    </TreeLegend>
                    <AccountTree profile={profile} isAr={isAr} />
                  </TreeCard>
                )}

                {profile.systems.filter((s) => s.linked && s.records && s.records.length > 0).map((s) => {
                  const Icon = ICONS[s.icon] || FileText;
                  // Dynamic columns from the domain pack; fall back to the legacy 4-column shape.
                  const cols = (s.columns && s.columns.length)
                    ? s.columns
                    : [
                        { key: 'ref', label_ar: 'المرجع', label_en: 'Reference' },
                        { key: 'date', label_ar: 'التاريخ', label_en: 'Date' },
                        { key: 'region', label_ar: 'المنطقة', label_en: 'Region' },
                        { key: 'detail', label_ar: 'التفاصيل', label_en: 'Detail' },
                      ];
                  return (
                    <SystemBlock key={s.key}>
                      <SystemTitle $c={s.color}>
                        <Icon />{isAr ? s.label_ar : s.label_en}
                        <SystemCount>{s.count}</SystemCount>
                      </SystemTitle>
                      <TableScroll>
                        <Table>
                          <thead>
                            <tr>
                              {cols.map((c) => (
                                <th key={c.key}>{isAr ? (c.label_ar || c.label_en) : (c.label_en || c.label_ar)}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {s.records.map((r, i) => (
                              <tr key={i}>
                                {cols.map((c) => {
                                  const v = r[c.key];
                                  return <td key={c.key}>{(v === null || v === undefined || v === '') ? '—' : String(v)}</td>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableScroll>
                    </SystemBlock>
                  );
                })}
              </>
            )}
        </>
      )}
    </Container>
  );
}
